import { useEffect, useState } from "react";
import { updatePersonalDialogue } from "../../Services/explore";
import { getVocabulary } from "../../Services/dialogueCreations";
import { showErrorToast, showSuccessToast } from "../../Utils/toast";
import { PERSONAL_COPY } from "./copy";
import "./personal.css";

/**
 * The Personal Dialogue part of the Overview (DCI v27 Part I §3; aris-ai plan 53).
 *
 * Renders the server's `personal` block in v27 order: source details, Summary, Key
 * Takeaways, Proposed Actions & Commitments (suggestions — never checked off here; they
 * become commitments only when the member agrees during the Dialogue). Research,
 * Start/Continue and the lifecycle controls stay where the Overview already draws them.
 *
 * Edit: title, Source, Provided By, Relationship, Summary, Takeaways and proposals. The
 * server answers with the updated block; a title edit never changes `content_version`,
 * any other content change does (and marks research stale). Capture Method is fixed.
 */

const CAPTURE_LABEL = {
  RECORDING: "Record Live",
  DOCUMENT: "Upload a Document",
  PHOTO: "Upload a Photo",
  TEXT: "Type or Paste Text",
};

const lines = (text) =>
  String(text || "")
    .split("\n")
    .map((t) => t.trim())
    .filter(Boolean);

/** Only the fields that changed, shaped for `PATCH /personal-dialogues/{id}`. */
export const editPayload = (personal, form) => {
  const out = {};
  const source = personal?.source || {};
  if (form.title.trim() && form.title.trim() !== (personal?.title || "")) {
    out.title = form.title.trim();
  }
  if (form.source_category && form.source_category !== source.source_category) {
    out.source_category = form.source_category;
  }
  if ((form.provided_by || "") !== (source.provided_by || "")) {
    out.provided_by = form.provided_by.trim();
  }
  if ((form.relationship || "") !== (source.relationship || "")) {
    out.relationship = form.relationship.trim();
  }
  if (form.summary.trim() !== (personal?.summary || "")) out.summary = form.summary.trim();
  const takeaways = lines(form.takeaways);
  if (takeaways.join("\n") !== (personal?.takeaways || []).join("\n")) {
    out.takeaways = takeaways;
  }
  const actions = lines(form.proposed_actions);
  const before = (personal?.proposed_actions || []).map((a) => a.text);
  if (actions.join("\n") !== before.join("\n")) {
    out.proposed_actions = actions.map((text) => ({ text }));
  }
  const contentChange = Object.keys(out).some((k) => k !== "title");
  if (contentChange) out.expected_content_version = personal?.content_version || 1;
  return out;
};

function EditSheet({ dialogueId, personal, categories, onSaved, onClose }) {
  const source = personal.source || {};
  const [form, setForm] = useState({
    title: personal.title || "",
    source_category: source.source_category || "",
    provided_by: source.provided_by || "",
    relationship: source.relationship || "",
    summary: personal.summary || "",
    takeaways: (personal.takeaways || []).join("\n"),
    proposed_actions: (personal.proposed_actions || []).map((a) => a.text).join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const category = categories.find((c) => c.value === form.source_category);
  const showRelationship = category ? category.shows_relationship : source.shows_relationship;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    const payload = editPayload(personal, form);
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const next = await updatePersonalDialogue(dialogueId, payload);
      onSaved(next);
      showSuccessToast(PERSONAL_COPY.saved);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      showErrorToast(
        code === "CONTENT_VERSION_MISMATCH" ? PERSONAL_COPY.conflict : PERSONAL_COPY.saveError
      );
      if (code === "CONTENT_VERSION_MISMATCH") onSaved(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pd-sheet" role="dialog" aria-modal="true" aria-label={PERSONAL_COPY.edit}>
      <div className="pd-sheet__panel">
        <h2 className="pd-sheet__title">{PERSONAL_COPY.edit}</h2>
        <label className="pd-field">
          <span>{PERSONAL_COPY.title}</span>
          <input value={form.title} maxLength={60} onChange={set("title")} />
        </label>
        {categories.length > 0 && (
          <label className="pd-field">
            <span>{PERSONAL_COPY.source}</span>
            <select value={form.source_category} onChange={set("source_category")}>
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="pd-field">
          <span>{PERSONAL_COPY.providedBy}</span>
          <input value={form.provided_by} maxLength={200} onChange={set("provided_by")} />
        </label>
        {showRelationship && (
          <label className="pd-field">
            <span>{PERSONAL_COPY.relationship}</span>
            <input value={form.relationship} maxLength={120} onChange={set("relationship")} />
          </label>
        )}
        <label className="pd-field">
          <span>{PERSONAL_COPY.summary}</span>
          <textarea rows={4} value={form.summary} onChange={set("summary")} />
        </label>
        <label className="pd-field">
          <span>{PERSONAL_COPY.takeaways} — {PERSONAL_COPY.onePerLine}</span>
          <textarea rows={4} value={form.takeaways} onChange={set("takeaways")} />
        </label>
        <label className="pd-field">
          <span>{PERSONAL_COPY.proposals} — {PERSONAL_COPY.onePerLine}</span>
          <textarea rows={4} value={form.proposed_actions} onChange={set("proposed_actions")} />
        </label>
        <p className="pd-sheet__note">{PERSONAL_COPY.staleNote}</p>
        <div className="pd-sheet__actions">
          <button type="button" className="pd-btn pd-btn--ghost" onClick={onClose} disabled={busy}>
            {PERSONAL_COPY.cancel}
          </button>
          <button type="button" className="pd-btn" onClick={save} disabled={busy}>
            {busy ? PERSONAL_COPY.saving : PERSONAL_COPY.save}
          </button>
        </div>
      </div>
    </div>
  );
}

function PersonalOverview({ dialogueId, personal, onChange, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getVocabulary()
      .then((v) => !cancelled && setCategories(v?.source_categories || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!personal) return null;
  const source = personal.source || {};
  const categoryLabel =
    categories.find((c) => c.value === source.source_category)?.label || null;
  const facts = [
    { key: "capture", label: PERSONAL_COPY.captureMethod, value: CAPTURE_LABEL[source.capture_method] },
    { key: "source", label: PERSONAL_COPY.source, value: categoryLabel },
    { key: "provided", label: PERSONAL_COPY.providedBy, value: source.provided_by },
    source.shows_relationship && {
      key: "relationship",
      label: PERSONAL_COPY.relationship,
      value: source.relationship,
    },
  ].filter((f) => f && f.value);

  return (
    <section className="pd" aria-label={PERSONAL_COPY.sectionLabel}>
      <div className="pd__head">
        <dl className="pd__facts">
          {facts.map((f) => (
            <div key={f.key} className="pd__fact">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        <button type="button" className="pd__edit" onClick={() => setEditing(true)}>
          {PERSONAL_COPY.edit}
        </button>
      </div>
      {source.confirmation !== "MEMBER_CONFIRMED" && (
        <p className="pd__unverified">{PERSONAL_COPY.unconfirmed}</p>
      )}

      {personal.summary && (
        <div className="pd__block">
          <h3>{PERSONAL_COPY.summary}</h3>
          <p>{personal.summary}</p>
        </div>
      )}
      {personal.takeaways?.length > 0 && (
        <div className="pd__block">
          <h3>{PERSONAL_COPY.takeaways}</h3>
          <ul>
            {personal.takeaways.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
      )}
      {personal.proposed_actions?.length > 0 && (
        <div className="pd__block">
          <h3>{PERSONAL_COPY.proposals}</h3>
          <ul className="pd__proposals">
            {personal.proposed_actions.map((a) => (
              <li key={a.text}>
                <span className="pd__circle" aria-hidden="true" />
                {a.text}
              </li>
            ))}
          </ul>
          <p className="pd__note">{PERSONAL_COPY.proposalsNote}</p>
        </div>
      )}

      {editing && (
        <EditSheet
          dialogueId={dialogueId}
          personal={personal}
          categories={categories}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            if (next) onChange(next);
            else onRefresh();
          }}
        />
      )}
    </section>
  );
}

export default PersonalOverview;
